(function () {
  'use strict';

  // ── The offer-resolution + suppression spine (the "blackout" logic) ──
  //
  // Pure, DOM-free, side-effect-free. Given a cart and the offer registry, it
  // returns which ONE campaign is active for this cart, which campaigns are
  // suppressed (blacked out), and the always-on baselines. The cart drawer /
  // page code calls this, then applies only the active offer's reward and
  // hides the suppressed ones — so a bundle can black out a GWP cleanly, in one
  // place, instead of scattered per-offer hacks.
  //
  // Config source is the offer REGISTRY (today a stand-in object; later the
  // Shopify `offer` metaobjects). The resolver doesn't care where it comes from.

  // Exact-match line_property values = the preconfig bundles' stamps; derived
  // from the offers list so the exclusion below stays pure and testable.
  function preconfigStampSet(offers) {
    var out = {};
    (offers || []).forEach(function (o) {
      var t = o.trigger || {};
      if (t.type === 'line_property' && t.value != null) out[String(t.value)] = 1;
    });
    return out;
  }

  function triggerMatches(trigger, cart, stamps) {
    if (!trigger) return true;
    var items = (cart && cart.items) || [];
    switch (trigger.type) {
      case 'subtotal':
        return (cart.total_price || 0) >= (trigger.min || 0);
      case 'products_all': {
        // every listed product must be present in the cart (the bundle case)
        var have = {};
        items.forEach(function (i) { have[i.product_id] = (have[i.product_id] || 0) + (i.quantity || 1); });
        return (trigger.productIds || []).every(function (id) { return have[id] > 0; });
      }
      case 'collection_qty': {
        // N units from a set of product ids (the "% off 2+" case).
        // Sweep #4: EXCLUDE gift lines and preconfig-stamped lines — the same
        // rule bundleQty/the drawer/the builder count by. Without this, a gift
        // lamp + 1 paid lamp activated the campaign the applicator then earned
        // nothing for, and preconfig carts could activate the generic offer.
        var q = 0;
        items.forEach(function (i) {
          var props = i.properties || {};
          if (props._offer_gift || props._gift_item === 'true') return;
          if (props._bundle && stamps && stamps[String(props._bundle)]) return;
          if ((trigger.productIds || []).indexOf(i.product_id) >= 0) q += (i.quantity || 1);
        });
        return q >= (trigger.minQty || 1);
      }
      case 'line_property': {
        // a cart LINE carries a given property (the builder-bundle case): the
        // set items are stamped properties[_bundle]=<offer-id> by bundle-builder.js,
        // so this detects "a builder bundle is in the cart" precisely (not a fuzzy
        // collection count). With trigger.value set, requires an exact match.
        return items.some(function (i) {
          var v = (i.properties || {})[trigger.key];
          if (trigger.value != null) return v === trigger.value;
          return v != null && v !== '';
        });
      }
      default:
        return false;
    }
  }

  // A "cart-content OVERRIDE" trigger is a HARD cart signal that overrides the
  // session/landing pick (the builder bundle). subtotal/none are NOT overrides —
  // subtotal is a GWP's REWARD THRESHOLD (when the gift adds), checked by the
  // applicator, not a selection signal. This split is what lets a GWP be chosen by
  // LANDING while its $100 gift still fires on cart value.
  function isOverrideTrigger(trigger) {
    if (!trigger) return false;
    return trigger.type === 'line_property' || trigger.type === 'collection_qty' || trigger.type === 'products_all';
  }

  // Offer windows (window_start/window_end from the metaobject): an offer outside
  // its window behaves as disabled. `now` injectable for tests.
  //
  // A DATE-ONLY edge ("2026-08-31") means the CALENDAR DAY: start-of-day for
  // window_start, END-of-day INCLUSIVE for window_end — matching the Liquid
  // renderer (fixed-bundle.liquid adds 86399s). Bare new Date('2026-08-31') is
  // midnight UTC = Aug 30 8pm ET, which sold the offer's last day at full price
  // (sweep C1). Appending a time makes the browser parse it as LOCAL time.
  var DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
  function edgeTime(v, endOfDay) {
    var s = String(v);
    if (DATE_ONLY.test(s)) s += endOfDay ? 'T23:59:59' : 'T00:00:00';
    return new Date(s).getTime();
  }
  function inWindow(o, now) {
    var w = o && o.window;
    if (!w) return true;
    var t = now != null ? now : Date.now();
    if (w.start && t < edgeTime(w.start, false)) return false;
    if (w.end && t > edgeTime(w.end, true)) return false;
    return true;
  }

  function eligibleCampaigns(offers, now) {
    return (offers || []).filter(function (o) {
      return !o.baseline && o.enabled !== false && inWindow(o, now);
    });
  }

  function tag(o, by) {
    var clone = {};
    for (var k in o) { if (Object.prototype.hasOwnProperty.call(o, k)) clone[k] = o[k]; }
    clone.resolvedBy = by;
    return clone;
  }

  // ── THE SITEWIDE (SESSION) LAYER — Sean's model, 2026-08-11 ──
  //
  // "Whatever the active full-site offer is should be respected" on every
  // evergreen/navigable surface: sticky session → landing page (a BLACKOUT page
  // OVERWRITES the sitewide offer for the session — that's what blackout means)
  // → the site-wide default. Cart mechanics (builder tiers, preconfig sets) ride
  // ON TOP of this layer; they never replace it. The GIFT keys off THIS layer —
  // which is why a builder set stacks with the GWP (session is still the GWP)
  // while a preconfig-bundle session doesn't (its landing replaced the GWP).
  function resolveSession(cart, offers, opts) {
    cart = cart || {};
    opts = opts || {};
    var attrs = cart.attributes || {};
    var campaigns = eligibleCampaigns(offers, opts.now);
    function byId(id) { return campaigns.filter(function (o) { return o.id === id; })[0] || null; }

    // sticky session (first-touch, persisted on cart.attributes._offer).
    // LANDING OVERWRITES: a landing page that OWNS a different (eligible) offer
    // replaces the sticky session — "with the blackout landing pages, we're
    // overwriting the sitewide offer" (Sean's model, 2026-08-11). This covers
    // both the home-first default capture AND a sticky non-default session
    // (sweep C2/H3); off a landing page, the session stays sticky.
    if (attrs._offer) {
      var sess = byId(attrs._offer);
      var landingOwns = opts.pageOffer && opts.pageOffer !== attrs._offer && byId(opts.pageOffer);
      if (sess && !landingOwns) return tag(sess, 'session');
    }
    // the landing page owns an offer (a blackout page) — overwrites the sitewide layer.
    if (opts.pageOffer) {
      var page = byId(opts.pageOffer);
      if (page) return tag(page, 'landing');
    }
    // the site-wide default.
    var def = campaigns.filter(function (o) { return o.is_default; })[0];
    if (def) return tag(def, 'default');
    return null;
  }

  // ── SELECTION: the offer whose copy/reward the PRIMARY surfaces show ──
  // Precedence: ?offer= override → cart-content CAMPAIGN (builder/preconfig set —
  // a hard cart signal) → the sitewide session layer (resolveSession).
  // opts: { override?:id (?offer=), pageOffer?:id, now?:ms (tests) }
  function resolveOffer(cart, offers, opts) {
    cart = cart || {};
    opts = opts || {};
    var campaigns = eligibleCampaigns(offers, opts.now);

    // 0. explicit ?offer= override — deep link / A-B / QA. Only if enabled.
    if (opts.override) {
      var forced = campaigns.filter(function (o) { return o.id === opts.override; })[0];
      if (forced) return tag(forced, 'url');
    }
    // 1. cart-content campaign (builder/preconfig set) — highest-priority match.
    var stamps = preconfigStampSet(offers);
    var over = campaigns.filter(function (o) { return isOverrideTrigger(o.trigger) && triggerMatches(o.trigger, cart, stamps); });
    over.sort(function (a, b) { return (b.priority || 0) - (a.priority || 0); });
    if (over[0]) return tag(over[0], 'trigger');
    // 2. the sitewide session layer.
    return resolveSession(cart, offers, opts);
  }

  // Returns { baseline: [ids], active: id|null, suppressed: [ids], offer: <obj|null>, resolvedBy }
  // `active`/`suppressed`/`baseline` (the reward layer) are UNCHANGED — existing
  // consumers (cart-drawer isGiftSuppressed, tests) read those. `offer`/`resolvedBy`
  // are additive: the resolved DISPLAY offer object (see resolveOffer).
  function resolve(cart, offers, opts) {
    cart = cart || {};
    offers = offers || [];

    var baseline = offers.filter(function (o) { return o.baseline; }).map(function (o) { return o.id; });
    // disabled / out-of-window offers never activate the reward/suppression layer —
    // a blackout must not fire for an offer the admin turned off or that expired.
    var campaigns = eligibleCampaigns(offers, (opts || {}).now);

    // which campaigns does this cart qualify for?
    var stampSet = preconfigStampSet(offers);
    var triggered = campaigns.filter(function (o) { return triggerMatches(o.trigger, cart, stampSet); });

    // one-per-session: the highest-priority triggered campaign wins
    triggered.sort(function (a, b) { return (b.priority || 0) - (a.priority || 0); });
    var active = triggered[0] || null;

    var suppressed = [];
    if (active) {
      var sup = {};
      // the winner suppresses every OTHER triggered campaign...
      triggered.forEach(function (o) { if (o.id !== active.id) sup[o.id] = true; });
      // ...plus any explicit blackout it declares (even offers not currently triggered)
      (active.suppression || []).forEach(function (id) { if (id !== active.id) sup[id] = true; });
      suppressed = Object.keys(sup);
    }

    var offer = resolveOffer(cart, offers, opts);
    var sessionOffer = resolveSession(cart, offers, opts);
    return {
      baseline: baseline,
      active: active ? active.id : null,
      suppressed: suppressed,
      offer: offer,
      sessionOffer: sessionOffer,
      resolvedBy: offer ? offer.resolvedBy : null
    };
  }

  // ── PUBLISH: make the resolved offer the ONE signal surfaces read ──
  // Impure convenience wrapper (reads the URL, writes window/document) around the
  // pure resolver. Sets window.__activeOffer to the resolved offer object and
  // fires an `offer:resolved` DOM event so any surface — cart drawer, bundle page,
  // and third-party embeds like Alia (which can read window.* in a custom-code
  // condition) — re-renders the SAME copy. Call it on load and on every cart change.
  function firstTouchGet() {
    try { return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('__offer') : null; } catch (e) { return null; }
  }
  function firstTouchSet(id) {
    try { if (typeof sessionStorage !== 'undefined') sessionStorage.setItem('__offer', id); } catch (e) {}
  }

  function publish(cart, offers, opts) {
    cart = cart || {};
    opts = opts || {};
    if (opts.override == null && typeof location !== 'undefined' && location.search) {
      try { opts.override = new URLSearchParams(location.search).get('offer') || null; } catch (e) {}
    }
    // A blackout page declares the offer it owns via window.__pageOffer (set by its
    // template). First touch there selects that offer for the session.
    if (opts.pageOffer == null && typeof window !== 'undefined') {
      opts.pageOffer = window.__pageOffer || null;
    }
    // First-touch stickiness across navigation BEFORE a cart exists: sessionStorage
    // buffers the session pick and is injected as the sticky offer when the cart
    // carries none yet. cart.attributes._offer (written on the first cart mutation,
    // rides to the order) is the durable carrier and takes precedence over this.
    var ft = firstTouchGet();
    if (ft && !(cart.attributes && cart.attributes._offer)) {
      var merged = {};
      for (var k in cart) { if (Object.prototype.hasOwnProperty.call(cart, k)) merged[k] = cart[k]; }
      merged.attributes = Object.assign({}, cart.attributes, { _offer: ft });
      cart = merged;
    }

    var result = resolve(cart, offers, opts);

    // Buffer the session pick: a LANDING page always overwrites (blackout =
    // overwrite — a stale default in the buffer must not outlive the landing;
    // sweep C2), the DEFAULT is captured only on a fresh session. Never a
    // transient cart campaign.
    var sessBy = result.sessionOffer && result.sessionOffer.resolvedBy;
    if (result.sessionOffer && (sessBy === 'landing' || (!ft && sessBy === 'default'))) {
      firstTouchSet(result.sessionOffer.id);
    }

    if (typeof window !== 'undefined') {
      // TWO published layers (Sean's model): __sessionOffer = the sitewide offer
      // for this session (what the GIFT + sitewide messaging key off);
      // __activeOffer = the primary display offer (cart campaign when one is
      // triggered, else the session offer) — what set-group/discount surfaces read.
      window.__activeOffer = result.offer;
      window.__sessionOffer = result.sessionOffer;
      if (typeof document !== 'undefined' && typeof CustomEvent === 'function') {
        document.dispatchEvent(new CustomEvent('offer:resolved', {
          detail: { offer: result.offer, sessionOffer: result.sessionOffer }
        }));
      }
    }
    return result.offer;
  }

  window.OfferResolver = {
    resolve: resolve,
    resolveOffer: resolveOffer,
    resolveSession: resolveSession,
    inWindow: inWindow,
    triggerMatches: triggerMatches,
    publish: publish
  };

  // ── AUTO-INIT: publish once on load from the global registry ──
  // snippets/offer-registry.liquid (inline, runs before this deferred script)
  // defines window.OfferRegistry + a server-rendered window.__cartSnapshot, so we
  // publish the CORRECT offer on first paint — no cart fetch, accurate for a
  // returning user who already has a bundle in cart. cart-drawer.js republishes
  // window.__activeOffer on every subsequent cart change.
  function init() {
    var reg = window.OfferRegistry;
    if (!reg || !reg.length) return;
    var cart = window.__cartSnapshot || { items: [], total_price: 0 };
    publish(cart, reg);
  }
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})();
